/**
 * Централизованная система логирования
 * 
 * В production отключает debug/info логи для уменьшения размера бандла
 * Добавляет префиксы для разных модулей для лучшей читаемости
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  module?: string;
  enableDebug?: boolean;
  enableInfo?: boolean;
}

class Logger {
  private isProduction: boolean;
  private enableDebug: boolean;
  private enableInfo: boolean;

  constructor() {
    this.isProduction = import.meta.env.PROD || false;
    this.enableDebug = !this.isProduction;
    this.enableInfo = !this.isProduction;
  }

  /**
   * Форматирует префикс для лога
   */
  private formatPrefix(level: LogLevel, module?: string): string {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    const levelPrefix = {
      debug: '[DEBUG]',
      info: '[INFO]',
      warn: '[WARN]',
      error: '[ERROR]',
    }[level];

    if (module) {
      return `${timestamp} ${levelPrefix} [${module}]`;
    }
    return `${timestamp} ${levelPrefix}`;
  }

  /**
   * Debug лог - только в development
   */
  debug(message: string, ...args: unknown[]): void {
    if (this.enableDebug) {
      console.log(this.formatPrefix('debug'), message, ...args);
    }
  }

  /**
   * Info лог - только в development
   */
  info(message: string, ...args: unknown[]): void {
    if (this.enableInfo) {
      console.log(this.formatPrefix('info'), message, ...args);
    }
  }

  /**
   * Warning лог - всегда
   */
  warn(message: string, ...args: unknown[]): void {
    console.warn(this.formatPrefix('warn'), message, ...args);
  }

  /**
   * Error лог - всегда
   */
  error(message: string, error?: unknown, ...args: unknown[]): void {
    if (error) {
      console.error(this.formatPrefix('error'), message, error, ...args);
    } else {
      console.error(this.formatPrefix('error'), message, ...args);
    }
  }

  /**
   * Создает логгер для конкретного модуля
   */
  module(moduleName: string): ModuleLogger {
    return new ModuleLogger(this, moduleName);
  }
}

/**
 * Логгер для конкретного модуля
 */
class ModuleLogger {
  constructor(private logger: Logger, private moduleName: string) {}

  debug(message: string, ...args: unknown[]): void {
    if (this.logger['enableDebug']) {
      console.log(this.logger['formatPrefix']('debug', this.moduleName), message, ...args);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.logger['enableInfo']) {
      console.log(this.logger['formatPrefix']('info', this.moduleName), message, ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    console.warn(this.logger['formatPrefix']('warn', this.moduleName), message, ...args);
  }

  error(message: string, error?: unknown, ...args: unknown[]): void {
    if (error) {
      console.error(this.logger['formatPrefix']('error', this.moduleName), message, error, ...args);
    } else {
      console.error(this.logger['formatPrefix']('error', this.moduleName), message, ...args);
    }
  }
}

// Singleton instance
export const logger = new Logger();

// Экспортируем для удобства
export default logger;

